import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { StateUtils, StateWithProcess } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth';
import { OrderEntry, PrePrintedLabel } from '../../../../core/model';
import { CartType } from '../../../../core/model/cart.model';
import {
  getProcessErrorFactory,
  getProcessLoadingFactory,
  getProcessStateFactory,
} from '../../../../core/process/store/selectors/process.selectors';
import { ActiveCartFacade, CartPrePrintedLabelsFacade } from '../facades';
import { CartActions } from '../store/actions';
import { DELETE_PRE_PRINTED_LABEL_PROCESS, StateWithCart, UPDATE_PRE_PRINTED_LABEL_PROCESS } from '../store/cart-state';
import {
  getPrePrintedLabelInnerPackageQuantityRequiredLoading,
  getPrePrintedLabelInnerPackageQuantityRequiredState,
  getPrePrintedLabelOptionsLoading,
  getPrePrintedLabelOptionsState,
} from '../store/selectors/pre-printed-label.selector';

@Injectable()
export class CartPrePrintedLabelsService implements CartPrePrintedLabelsFacade {
  constructor(
    private store: Store<StateWithCart & StateWithProcess<any>>,
    private userIdService: UserIdService,
    private activeCartService: ActiveCartFacade
  ) {}

  // Options
  getOptions(cartType: CartType, entry: OrderEntry): Observable<PrePrintedLabel[]> {
    this.assertStockCart(cartType);

    return this.store.select(getPrePrintedLabelOptionsState(entry)).pipe(
      tap((state) => {
        if (this.needsToLoad(state)) {
          this.loadOptions(cartType, entry);
        }
      }),
      map((state) => state?.value)
    );
  }

  getLoadingOptions(cartType: CartType, entry: OrderEntry): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.store.select(getPrePrintedLabelOptionsLoading(entry));
  }

  private loadOptions(cartType: CartType, entry: OrderEntry): void {
    this.assertStockCart(cartType);

    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.store.dispatch(
          new CartActions.LoadPrePrintedOptions({
            cartType: cartType,
            cartId: cartId,
            userId: userId,
            entry,
          })
        );
      });
  }

  // Inner package quantity required
  getInnerPackageQuantityRequired(cartType: CartType, entry: OrderEntry): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.store.select(getPrePrintedLabelInnerPackageQuantityRequiredState(entry)).pipe(
      tap((state) => {
        if (this.needsToLoad(state)) {
          this.loadInnerPackageQuantityRequired(cartType, entry);
        }
      }),
      map((state) => state?.value || false)
    );
  }

  getLoadingInnerPackageQuantityRequired(cartType: CartType, entry: OrderEntry): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.store.select(getPrePrintedLabelInnerPackageQuantityRequiredLoading(entry));
  }

  private loadInnerPackageQuantityRequired(cartType: CartType, entry: OrderEntry): void {
    this.assertStockCart(cartType);

    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.store.dispatch(
          new CartActions.LoadPrePrintedInnerPackageQuantityRequired({
            cartType: cartType,
            cartId: cartId,
            userId: userId,
            entry,
          })
        );
      });
  }

  // Update
  updateLabel(cartType: CartType, entry: OrderEntry, option: PrePrintedLabel): void {
    this.assertStockCart(cartType);

    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.store.dispatch(
          new CartActions.UpdatePrePrintedLabel({
            cartType: cartType,
            cartId: cartId,
            userId: userId,
            entry,
            option: option,
          })
        );
      });
  }

  getUpdateLabelLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(UPDATE_PRE_PRINTED_LABEL_PROCESS)));
  }

  getUpdateLabelResult(): Observable<any> {
    return this.store.pipe(
      select(getProcessStateFactory(UPDATE_PRE_PRINTED_LABEL_PROCESS)),
      map((state: StateUtils.LoaderState<any>) => StateUtils.loaderValueSelector<any>(state))
    );
  }

  getUpdateLabelError(): Observable<any> {
    return this.store.pipe(select(getProcessErrorFactory(UPDATE_PRE_PRINTED_LABEL_PROCESS)));
  }

  reseUpdateLabelProcessingStates(): void {
    this.store.dispatch(new CartActions.UpdatePrePrintedLabelReset());
  }

  // Delete
  deleteLabel(cartType: CartType, entry: OrderEntry): void {
    this.assertStockCart(cartType);

    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.store.dispatch(
          new CartActions.DeletePrePrintedLabel({
            cartType: cartType,
            cartId: cartId,
            userId: userId,
            entry,
          })
        );
      });
  }

  getDeleteLabelLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(DELETE_PRE_PRINTED_LABEL_PROCESS)));
  }

  getDeleteLabelResult(): Observable<any> {
    return this.store.pipe(
      select(getProcessStateFactory(DELETE_PRE_PRINTED_LABEL_PROCESS)),
      map((state: StateUtils.LoaderState<any>) => StateUtils.loaderValueSelector<any>(state))
    );
  }

  getDeleteLabelError(): Observable<any> {
    return this.store.pipe(select(getProcessErrorFactory(DELETE_PRE_PRINTED_LABEL_PROCESS)));
  }

  reseDeleteLabelProcessingStates(): void {
    this.store.dispatch(new CartActions.UpdatePrePrintedLabelReset());
  }

  // Global
  private needsToLoad(loaderState: StateUtils.LoaderState<PrePrintedLabel[] | PrePrintedLabel | boolean>) {
    return !loaderState?.success && !loaderState?.error && !loaderState?.loading;
  }

  private assertStockCart(cartType: CartType) {
    if (cartType !== CartType.stock) {
      throw new Error('Pre printed label is only possible for stock carts');
    }
  }
}
